/**
 * Experimental windowed ("desktop") UI.
 *
 * The app's workspace (variables strip + editor/preview/scripts row) is a
 * singleton wired up by element id all over app.ts. Rather than duplicating
 * it per file, windowed mode turns every open tab into a floating, movable,
 * resizable window frame and *reparents the one live workspace into whichever
 * window is active*. Every other window shows a scrollable, read-only
 * snapshot of the whole workspace as it last looked for that file (editor,
 * preview and scripts panels included), so reference material can sit next
 * to the file being edited on a large screen. Each window also remembers its
 * own panel layout (edit mode, docs/scripts/variables visibility, dragged
 * panel sizes) and re-applies it when it becomes active.
 *
 * Clicking anywhere in an inactive window (or its tab in the tab bar, which
 * doubles as the taskbar) activates it: the regular tab-switch path runs and
 * the workspace moves into that frame. Maximizing a window fills the desktop,
 * which is exactly the classic single-file view.
 */

import type { TabManager } from '../tabs/TabManager.js';
import {
  WindowLayout,
  RESIZE_EDGES,
  moveRect,
  resizeRect,
  type PanelState,
  type Rect,
  type ResizeEdge,
  type Size,
  type WindowPersistence,
} from './window-layout.js';

export const WINDOWED_MODE_KEY = 'leedoc_windowed_mode';
export const WINDOW_LAYOUT_KEY = 'leedoc_windows';

export interface DesktopHost {
  /** The `#workspace` element (variables panel + content container). */
  workspace: HTMLElement;
  /** The live preview element (`#markdown-output`); its scroll position is resumed on activation. */
  preview: HTMLElement;
  /** Current panel layout of the live workspace (stored per window on every switch). */
  getPanelState: () => PanelState;
  /** Re-apply a window's remembered panel layout once the workspace has moved into it. */
  applyPanelState: (state: PanelState) => void;
  tabManager: TabManager;
  getCurrentFile: () => string | null;
  /** Same handler as clicking a tab: snapshots the outgoing file and restores/loads the target. */
  activate: (path: string) => Promise<void>;
  /** Same handler as a tab's close button (saves first, keeps the tab on failure). */
  close: (path: string) => Promise<void>;
  getDisplayName: (path: string) => string;
  /** Receives the desktop's taskbar actions (Tile / Cascade / Exit) to render in the tab bar. */
  setTaskbarActions: (el: HTMLElement | null) => void;
  /** Fired after windowed mode is switched on or off (also via the taskbar's Exit button). */
  onModeChange?: (enabled: boolean) => void;
  /**
   * Render a lightweight preview (woven + markdown, no enrichers) for a tab
   * whose source is cached but which hasn't been rendered live since windowed
   * mode was switched on — so its window doesn't start out as "Click to open".
   */
  renderSnapshot?: (path: string) => Promise<string | null>;
}

interface Frame {
  path: string;
  el: HTMLElement;
  titleEl: HTMLElement;
  pathEl: HTMLElement;
  dirtyEl: HTMLElement;
  bodyEl: HTMLElement;
  btnMax: HTMLButtonElement;
  btnClose: HTMLButtonElement;
}

interface ScrollMemo {
  el: HTMLElement;
  top: number;
  left: number;
}

/**
 * Static copy of the workspace (or, for tabs never shown live since the mode
 * was enabled, a lightweight preview render) shown in an inactive window.
 */
interface WorkspaceSnapshot {
  el: HTMLElement;
  /** Scrollable descendants and where they were, re-applied whenever `el` is (re)attached. */
  scrolls: ScrollMemo[];
  /** The preview pane inside the snapshot, so activation can resume from where it was scrolled. */
  previewEl: HTMLElement | null;
}

/** Elements whose scroll position is always remembered, even when currently at 0. */
const SCROLL_MEMO_SELECTOR = '.cm-scroller, .markdown-content, textarea';

/** Overlay kept over a freshly activated window until its own preview has rendered. */
const ACTIVATION_OVERLAY_TIMEOUT_MS = 1500;
/** Pointer travel before a title-bar press on a maximized window counts as a drag. */
const DRAG_THRESHOLD_PX = 6;

export class DesktopManager {
  private host: DesktopHost;
  private layout = new WindowLayout();
  private frames = new Map<string, Frame>();
  private snapshots = new Map<string, WorkspaceSnapshot>();
  private desktopEl: HTMLElement | null = null;
  private dockEl: HTMLElement | null = null;
  private workspaceHome: { parent: HTMLElement; next: Node | null } | null = null;
  private taskbarActions: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private fitScheduled = false;
  private pendingScrollRestore: { path: string; scrollTop: number } | null = null;
  private activationOverlay: { path: string; el: HTMLElement; timer: number } | null = null;
  private enabled = false;

  constructor(host: DesktopHost) {
    this.host = host;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /** Whether the user left windowed mode on last time. */
  static isPersistedOn(): boolean {
    try {
      return localStorage.getItem(WINDOWED_MODE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  toggle(): boolean {
    if (this.enabled) this.disable(); else this.enable();
    return this.enabled;
  }

  enable(): void {
    if (this.enabled) return;
    const workspace = this.host.workspace;
    const parent = workspace.parentElement;
    if (!parent) return;
    this.enabled = true;
    try { localStorage.setItem(WINDOWED_MODE_KEY, 'true'); } catch { /* ignore */ }

    this.workspaceHome = { parent, next: workspace.nextSibling };

    const desktop = document.createElement('div');
    desktop.className = 'ld-desktop';
    const dock = document.createElement('div');
    dock.className = 'ld-workspace-dock';
    desktop.appendChild(dock);
    parent.insertBefore(desktop, workspace);
    dock.appendChild(workspace);
    this.desktopEl = desktop;
    this.dockEl = dock;
    document.body.classList.add('ld-windowed');

    this.taskbarActions = this.buildTaskbarActions();
    this.host.setTaskbarActions(this.taskbarActions);

    // Restore persisted geometry for tabs that are still open
    this.layout = new WindowLayout();
    this.layout.restore(this.loadPersisted(), this.host.tabManager.tabs.map(t => t.path), this.desktopSize());

    this.resizeObserver = new ResizeObserver(() => this.scheduleFit());
    this.resizeObserver.observe(desktop);

    this.sync();
    this.host.onModeChange?.(true);
  }

  disable(): void {
    if (!this.enabled) return;
    this.enabled = false;
    try { localStorage.setItem(WINDOWED_MODE_KEY, 'false'); } catch { /* ignore */ }

    this.persist();
    this.clearActivationOverlay();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    // Put the workspace back where it came from
    const home = this.workspaceHome;
    if (home) {
      home.parent.insertBefore(this.host.workspace, home.next && home.next.parentElement === home.parent ? home.next : null);
    }
    this.desktopEl?.remove();
    this.desktopEl = null;
    this.dockEl = null;
    this.workspaceHome = null;
    this.frames.clear();
    this.snapshots.clear();
    this.pendingScrollRestore = null;
    this.host.setTaskbarActions(null);
    this.taskbarActions = null;
    document.body.classList.remove('ld-windowed');
    this.host.onModeChange?.(false);
  }

  /**
   * Reconcile window frames with the tab manager: create frames for new tabs,
   * drop frames for closed ones, detect renames, and host the workspace in
   * the active tab's frame. Safe to call often (it is idempotent).
   */
  sync(): void {
    if (!this.enabled || !this.desktopEl) return;
    const tabs = this.host.tabManager.tabs;
    const open = new Set(tabs.map(t => t.path));
    const desktop = this.desktopSize();

    const gone = [...this.frames.keys()].filter(p => !open.has(p));
    const fresh = tabs.map(t => t.path).filter(p => !this.frames.has(p));

    // A single vanished path plus a single new one is a rename: keep geometry + snapshot
    if (gone.length === 1 && fresh.length === 1) {
      this.renameFrame(gone[0], fresh[0]);
      gone.length = 0;
      fresh.length = 0;
    }

    for (const path of gone) this.destroyFrame(path);
    for (const path of fresh) {
      this.layout.add(path, desktop);
      this.createFrame(path);
    }

    const active = this.host.tabManager.activeTab;
    if (active && this.frames.has(active)) {
      this.layout.bringToFront(active);
      this.layout.setMinimized(active, false);
      this.hostWorkspace(active);
    } else {
      this.parkWorkspace();
    }

    this.applyAll();
    this.refreshChrome();
    this.persist();
  }

  /** Update titles, dirty markers, active/minimized classes. Cheap; called on every tab-bar render. */
  refreshChrome(): void {
    if (!this.enabled) return;
    const active = this.host.tabManager.activeTab;
    for (const frame of this.frames.values()) {
      const tab = this.host.tabManager.getTab(frame.path);
      const state = this.layout.get(frame.path);
      frame.titleEl.textContent = this.host.getDisplayName(frame.path);
      frame.pathEl.textContent = this.subtitleFor(frame.path);
      frame.dirtyEl.classList.toggle('hidden', !tab?.hasUnsavedChanges);
      frame.el.classList.toggle('active', frame.path === active);
      frame.el.classList.toggle('minimized', !!state?.minimized);
      frame.el.classList.toggle('maximized', !!state?.maximized);
      frame.btnMax.title = state?.maximized ? 'Restore' : 'Maximize';
      frame.btnMax.innerHTML = state?.maximized ? '&#10064;' : '&#9744;';
      frame.btnClose.classList.toggle('hidden', this.host.tabManager.isPinned(frame.path));
    }
  }

  /**
   * Store a static copy of the live workspace as the snapshot for `path`, and
   * remember its panel layout. Called right before a tab switch (while the
   * workspace still shows `path`) so the window that is about to lose the
   * workspace keeps showing exactly what it showed.
   */
  captureWorkspace(path: string): void {
    if (!this.enabled) return;
    this.layout.setPanels(path, this.host.getPanelState());
    if (this.host.preview.querySelector(':scope > .placeholder')) return;
    this.snapshots.set(path, this.cloneWorkspace());
  }

  /** The panel layout remembered for a window, if it has hosted the workspace before. */
  panelStateFor(path: string): PanelState | null {
    return this.layout.get(path)?.panels ?? null;
  }

  private cloneWorkspace(): WorkspaceSnapshot {
    const src = this.host.workspace;
    const clone = src.cloneNode(true) as HTMLElement;
    const srcAll = src.querySelectorAll<HTMLElement>('*');
    const cloneAll = clone.querySelectorAll<HTMLElement>('*');
    const scrolls: ScrollMemo[] = [];
    for (let i = 0; i < srcAll.length; i++) {
      const a = srcAll[i];
      const b = cloneAll[i];
      if (!b) break;
      // Dirty form values aren't copied by cloneNode
      if (a instanceof HTMLTextAreaElement || a instanceof HTMLSelectElement || a instanceof HTMLInputElement) {
        (b as HTMLTextAreaElement | HTMLSelectElement | HTMLInputElement).value = a.value;
        if (a instanceof HTMLInputElement && (a.type === 'checkbox' || a.type === 'radio')) {
          (b as HTMLInputElement).checked = a.checked;
        }
      }
      if (a.scrollTop > 0 || a.scrollLeft > 0 || a.matches(SCROLL_MEMO_SELECTOR)) {
        scrolls.push({ el: b, top: a.scrollTop, left: a.scrollLeft });
      }
    }
    // The app wires everything by element id — the copy must not shadow the live elements.
    clone.removeAttribute('id');
    clone.querySelectorAll('[id]').forEach(el => {
      el.setAttribute('data-ld-id', el.id);
      el.removeAttribute('id');
    });
    clone.querySelectorAll('[contenteditable]').forEach(el => el.setAttribute('contenteditable', 'false'));
    clone.querySelectorAll('input, textarea, select').forEach(el => el.setAttribute('tabindex', '-1'));
    clone.classList.add('ld-window-snapshot', 'ld-snapshot-workspace');
    // Snapshots are inert: links/buttons/checkboxes inside must not fire.
    clone.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); }, true);
    return {
      el: clone,
      scrolls,
      previewEl: clone.querySelector<HTMLElement>('.markdown-panel .markdown-content'),
    };
  }

  /** Read back where the user scrolled a snapshot that is currently on screen. */
  private rememberScrolls(snap: WorkspaceSnapshot): void {
    for (const memo of snap.scrolls) {
      memo.top = memo.el.scrollTop;
      memo.left = memo.el.scrollLeft;
    }
  }

  private applyScrolls(snap: WorkspaceSnapshot): void {
    for (const memo of snap.scrolls) {
      memo.el.scrollTop = memo.top;
      memo.el.scrollLeft = memo.left;
    }
  }

  /** Called by the app after every preview render of the current file. */
  onPreviewRendered(): void {
    if (!this.enabled) return;
    const current = this.host.getCurrentFile();
    if (this.pendingScrollRestore) {
      if (this.pendingScrollRestore.path === current) {
        this.host.preview.scrollTop = this.pendingScrollRestore.scrollTop;
      }
      this.pendingScrollRestore = null;
    }
    if (this.activationOverlay && this.activationOverlay.path === current) {
      this.clearActivationOverlay();
    }
  }

  isMinimized(path: string): boolean {
    return !!this.layout.get(path)?.minimized;
  }

  /** Click on the already-active tab in the taskbar: restore it if minimized, else just raise it. */
  focusActive(): void {
    const active = this.host.tabManager.activeTab;
    if (!active) return;
    this.layout.setMinimized(active, false);
    this.layout.bringToFront(active);
    this.applyAll();
    this.refreshChrome();
    this.persist();
  }

  toggleMaximize(path = this.host.tabManager.activeTab): void {
    if (!path) return;
    const state = this.layout.get(path);
    if (!state) return;
    this.layout.setMaximized(path, !state.maximized);
    this.applyAll();
    this.refreshChrome();
    this.persist();
  }

  minimize(path: string): void {
    const state = this.layout.get(path);
    if (!state) return;
    this.layout.setMinimized(path, true);
    this.applyAll();
    this.refreshChrome();
    this.persist();
    // Focus passes to the next visible window, like a real window manager
    if (path === this.host.tabManager.activeTab) {
      const next = this.layout.frontVisible();
      if (next) void this.host.activate(next.path);
    }
  }

  tileAll(): void {
    this.layout.tileAll(this.desktopSize());
    this.applyAll();
    this.refreshChrome();
    this.persist();
  }

  cascadeAll(): void {
    this.layout.cascadeAll(this.desktopSize());
    this.applyAll();
    this.refreshChrome();
    this.persist();
  }

  // --------------------------------------------------------------------
  // Frames
  // --------------------------------------------------------------------

  private createFrame(path: string): Frame {
    const el = document.createElement('div');
    el.className = 'ld-window';
    el.dataset.path = path;

    const titlebar = document.createElement('div');
    titlebar.className = 'ld-window-titlebar';

    const icon = document.createElement('span');
    icon.className = 'ld-window-icon';
    icon.innerHTML = path.startsWith('_components/') ? COMPONENT_ICON : PAGE_ICON;

    const titleEl = document.createElement('span');
    titleEl.className = 'ld-window-title';
    const pathEl = document.createElement('span');
    pathEl.className = 'ld-window-path';
    const dirtyEl = document.createElement('span');
    dirtyEl.className = 'ld-window-dirty hidden';
    dirtyEl.title = 'Unsaved changes';
    dirtyEl.textContent = '●';

    const controls = document.createElement('div');
    controls.className = 'ld-window-controls';
    const btnMin = this.controlButton('&#8212;', 'Minimize');
    const btnMax = this.controlButton('&#9744;', 'Maximize');
    const btnClose = this.controlButton('&#10005;', 'Close');
    btnClose.classList.add('ld-window-btn-close');
    controls.append(btnMin, btnMax, btnClose);

    titlebar.append(icon, titleEl, pathEl, dirtyEl, controls);

    const bodyEl = document.createElement('div');
    bodyEl.className = 'ld-window-body';

    el.append(titlebar, bodyEl);
    for (const edge of RESIZE_EDGES) {
      const handle = document.createElement('div');
      handle.className = `ld-window-resizer ld-window-resizer-${edge}`;
      handle.dataset.edge = edge;
      handle.addEventListener('pointerdown', (e) => this.startResize(e, path, edge));
      el.appendChild(handle);
    }

    const frame: Frame = { path, el, titleEl, pathEl, dirtyEl, bodyEl, btnMax, btnClose };
    this.frames.set(path, frame);

    // Any press inside the window focuses it (capture phase so CodeMirror
    // still receives the event afterwards).
    el.addEventListener('pointerdown', () => this.focusWindow(frame.path), true);

    titlebar.addEventListener('pointerdown', (e) => this.startMove(e, frame.path));
    titlebar.addEventListener('dblclick', (e) => {
      if ((e.target as HTMLElement).closest('button')) return;
      this.toggleMaximize(frame.path);
    });

    btnMin.addEventListener('click', (e) => { e.stopPropagation(); this.minimize(frame.path); });
    btnMax.addEventListener('click', (e) => { e.stopPropagation(); this.toggleMaximize(frame.path); });
    btnClose.addEventListener('click', (e) => { e.stopPropagation(); void this.host.close(frame.path); });

    this.fillWithSnapshot(frame);
    this.desktopEl!.appendChild(el);
    return frame;
  }

  private controlButton(html: string, title: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ld-window-btn';
    btn.title = title;
    btn.innerHTML = html;
    // Don't let the title bar treat button presses as a drag start
    btn.addEventListener('pointerdown', (e) => e.stopPropagation());
    return btn;
  }

  private destroyFrame(path: string): void {
    const frame = this.frames.get(path);
    if (!frame) return;
    if (frame.bodyEl.contains(this.host.workspace)) this.parkWorkspace();
    if (this.activationOverlay?.path === path) this.clearActivationOverlay();
    frame.el.remove();
    this.frames.delete(path);
    this.layout.remove(path);
    this.snapshots.delete(path);
  }

  private renameFrame(oldPath: string, newPath: string): void {
    const frame = this.frames.get(oldPath);
    if (!frame) return;
    this.frames.delete(oldPath);
    frame.path = newPath;
    frame.el.dataset.path = newPath;
    this.frames.set(newPath, frame);
    this.layout.rename(oldPath, newPath);
    const snap = this.snapshots.get(oldPath);
    if (snap) {
      this.snapshots.delete(oldPath);
      this.snapshots.set(newPath, snap);
    }
    if (this.pendingScrollRestore?.path === oldPath) this.pendingScrollRestore.path = newPath;
    if (this.activationOverlay?.path === oldPath) this.activationOverlay.path = newPath;
  }

  /** Move the live workspace into `path`'s frame, leaving a snapshot behind in the previous host. */
  private hostWorkspace(path: string): void {
    const frame = this.frames.get(path);
    if (!frame) return;
    const workspace = this.host.workspace;
    if (frame.bodyEl.contains(workspace)) return;

    // Fill the outgoing host with its snapshot
    const prev = this.frameHostingWorkspace();
    if (prev) {
      // If the app hasn't switched files yet the live workspace is still this
      // file's — capture it now in case nobody did (belt and braces).
      if (!this.snapshots.has(prev.path) && this.host.getCurrentFile() === prev.path) {
        this.captureWorkspace(prev.path);
      } else {
        this.layout.setPanels(prev.path, this.host.getPanelState());
      }
      this.fillWithSnapshot(prev);
    } else {
      this.clearActivationOverlay();
    }

    // Keep the target's snapshot on screen (as an overlay) until the live
    // preview has rendered this file, so the window doesn't briefly show the
    // previous file's content.
    const snap = this.snapshots.get(path);
    const overlay = frame.bodyEl.querySelector<HTMLElement>(':scope > .ld-window-snapshot');
    // The user may have scrolled the read-only snapshot; resume from there
    if (snap && overlay === snap.el) this.rememberScrolls(snap);
    const resumeScrollTop = snap?.previewEl ? snap.previewEl.scrollTop : undefined;
    frame.bodyEl.querySelectorAll(':scope > .ld-window-placeholder').forEach(n => n.remove());
    this.clearActivationOverlay();
    frame.bodyEl.appendChild(workspace);
    if (overlay && snap && this.host.getCurrentFile() !== path) {
      overlay.classList.add('ld-window-overlay');
      const timer = window.setTimeout(() => this.clearActivationOverlay(), ACTIVATION_OVERLAY_TIMEOUT_MS);
      this.activationOverlay = { path, el: overlay, timer };
    } else {
      overlay?.remove();
    }
    if (resumeScrollTop !== undefined) this.pendingScrollRestore = { path, scrollTop: resumeScrollTop };

    // Each window keeps its own panel layout; a window that never hosted the
    // workspace inherits whatever the previous one showed.
    const panels = this.layout.get(path)?.panels;
    if (panels) this.host.applyPanelState(panels);
    else this.layout.setPanels(path, this.host.getPanelState());
  }

  private parkWorkspace(): void {
    if (!this.dockEl) return;
    const prev = this.frameHostingWorkspace();
    if (prev) this.fillWithSnapshot(prev);
    this.clearActivationOverlay();
    if (!this.dockEl.contains(this.host.workspace)) this.dockEl.appendChild(this.host.workspace);
  }

  private frameHostingWorkspace(): Frame | null {
    for (const frame of this.frames.values()) {
      if (frame.bodyEl.contains(this.host.workspace)) return frame;
    }
    return null;
  }

  /** Replace a frame body's content with the file's workspace snapshot (or a placeholder). */
  private fillWithSnapshot(frame: Frame): void {
    const body = frame.bodyEl;
    const snap = this.snapshots.get(frame.path);
    if (snap && snap.el.parentElement === body) this.rememberScrolls(snap);
    if (body.contains(this.host.workspace)) {
      // Workspace is being moved out by the caller; only clear leftovers here
      body.querySelectorAll(':scope > .ld-window-snapshot, :scope > .ld-window-placeholder').forEach(n => n.remove());
    } else {
      body.innerHTML = '';
    }
    if (snap) {
      snap.el.classList.remove('ld-window-overlay');
      body.appendChild(snap.el);
      this.applyScrolls(snap);
    } else {
      const ph = document.createElement('div');
      ph.className = 'ld-window-placeholder';
      ph.innerHTML = '<span>Click to open</span>';
      body.appendChild(ph);
      this.requestRenderedSnapshot(frame);
    }
  }

  /** Ask the app for a lightweight render of a cached-but-unrendered tab and show it if still relevant. */
  private requestRenderedSnapshot(frame: Frame): void {
    const render = this.host.renderSnapshot;
    if (!render) return;
    const path = frame.path;
    void render(path).then(html => {
      if (!html || !this.enabled) return;
      const current = this.frames.get(path);
      if (!current || current !== frame) return;
      if (this.snapshots.has(path) || current.bodyEl.contains(this.host.workspace)) return;
      const el = document.createElement('div');
      el.className = 'ld-window-snapshot ld-snapshot-lite markdown-content';
      el.innerHTML = html;
      el.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); }, true);
      this.snapshots.set(path, { el, scrolls: [{ el, top: 0, left: 0 }], previewEl: el });
      this.fillWithSnapshot(current);
    });
  }

  private clearActivationOverlay(): void {
    if (!this.activationOverlay) return;
    window.clearTimeout(this.activationOverlay.timer);
    this.activationOverlay.el.remove();
    this.activationOverlay = null;
  }

  // --------------------------------------------------------------------
  // Geometry
  // --------------------------------------------------------------------

  private desktopSize(): Size {
    const el = this.desktopEl;
    if (!el) return { w: window.innerWidth, h: window.innerHeight };
    return { w: Math.max(1, el.clientWidth), h: Math.max(1, el.clientHeight) };
  }

  private applyAll(): void {
    for (const frame of this.frames.values()) this.applyFrame(frame);
  }

  private applyFrame(frame: Frame): void {
    const state = this.layout.get(frame.path);
    if (!state) return;
    const { x, y, w, h } = state.rect;
    frame.el.style.left = `${x}px`;
    frame.el.style.top = `${y}px`;
    frame.el.style.width = `${w}px`;
    frame.el.style.height = `${h}px`;
    frame.el.style.zIndex = String(10 + state.z);
    frame.el.classList.toggle('maximized', state.maximized);
    frame.el.classList.toggle('minimized', state.minimized);
  }

  private scheduleFit(): void {
    if (this.fitScheduled) return;
    this.fitScheduled = true;
    requestAnimationFrame(() => {
      this.fitScheduled = false;
      if (!this.enabled) return;
      this.layout.fitAll(this.desktopSize());
      this.applyAll();
    });
  }

  private focusWindow(path: string): void {
    const active = this.host.tabManager.activeTab;
    if (path === active) {
      if (this.layout.bringToFront(path)) this.applyAll();
      return;
    }
    // Raise immediately for responsiveness; the tab switch moves the workspace
    this.layout.bringToFront(path);
    this.applyAll();
    void this.host.activate(path);
  }

  private startMove(e: PointerEvent, path: string): void {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('button')) return;
    const state = this.layout.get(path);
    const frame = this.frames.get(path);
    if (!state || !frame) return;
    e.preventDefault();

    let start: Rect = { ...state.rect };
    let wasMaximized = state.maximized;
    const grabRatio = e.offsetX / Math.max(1, frame.el.clientWidth);
    this.beginPointerDrag(e, frame.el, (dx, dy) => {
      if (wasMaximized) {
        // Dragging a maximized window un-maximizes it under the cursor — but
        // only once the pointer really moves, so a double-click stays a click.
        if (Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD_PX) return;
        wasMaximized = false;
        const desktop = this.desktopSize();
        const rect = this.layout.get(path)!.rect;
        this.layout.setMaximized(path, false);
        this.layout.setRect(path, { ...rect, x: Math.round(e.clientX - this.desktopLeft() - rect.w * grabRatio), y: 0 }, desktop);
        start = { ...this.layout.get(path)!.rect };
        this.refreshChrome();
      }
      this.layout.setRect(path, moveRect(start, dx, dy, this.desktopSize()), this.desktopSize());
      this.applyFrame(frame);
    });
  }

  private startResize(e: PointerEvent, path: string, edge: ResizeEdge): void {
    if (e.button !== 0) return;
    const state = this.layout.get(path);
    const frame = this.frames.get(path);
    if (!state || !frame || state.maximized) return;
    e.preventDefault();
    e.stopPropagation();
    const start: Rect = { ...state.rect };
    this.beginPointerDrag(e, frame.el, (dx, dy) => {
      this.layout.setRect(path, resizeRect(start, edge, dx, dy, this.desktopSize()), this.desktopSize());
      this.applyFrame(frame);
    });
  }

  private beginPointerDrag(e: PointerEvent, frameEl: HTMLElement, onDelta: (dx: number, dy: number) => void): void {
    const target = e.currentTarget as HTMLElement;
    const startX = e.clientX;
    const startY = e.clientY;
    target.setPointerCapture?.(e.pointerId);
    document.body.classList.add('ld-window-dragging');
    frameEl.classList.add('dragging');

    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== e.pointerId) return;
      onDelta(ev.clientX - startX, ev.clientY - startY);
    };
    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId !== e.pointerId) return;
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
      target.releasePointerCapture?.(e.pointerId);
      document.body.classList.remove('ld-window-dragging');
      frameEl.classList.remove('dragging');
      this.persist();
    };
    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
  }

  private desktopLeft(): number {
    return this.desktopEl?.getBoundingClientRect().left ?? 0;
  }

  // --------------------------------------------------------------------
  // Taskbar actions + persistence
  // --------------------------------------------------------------------

  private buildTaskbarActions(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'ld-taskbar-actions';
    const mk = (label: string, title: string, onClick: () => void) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'ld-taskbar-btn';
      b.textContent = label;
      b.title = title;
      b.addEventListener('click', onClick);
      wrap.appendChild(b);
      return b;
    };
    mk('Tile', 'Arrange all windows side by side', () => this.tileAll());
    mk('Cascade', 'Stack all windows in a cascade', () => this.cascadeAll());
    const exit = mk('Exit windows', 'Back to the classic single-file view', () => this.disable());
    exit.classList.add('ld-taskbar-btn-exit');
    return wrap;
  }

  private subtitleFor(path: string): string {
    const clean = path.replace(/^_components\//, '');
    const idx = clean.lastIndexOf('/');
    if (path.startsWith('_components/')) return idx === -1 ? 'component' : `component · ${clean.slice(0, idx)}`;
    return idx === -1 ? '' : clean.slice(0, idx);
  }

  private persist(): void {
    if (!this.enabled) return;
    // The active window's panel layout is only captured on switches; refresh it here too
    const current = this.host.getCurrentFile();
    if (current && this.frameHostingWorkspace()?.path === current) {
      this.layout.setPanels(current, this.host.getPanelState());
    }
    try {
      localStorage.setItem(WINDOW_LAYOUT_KEY, JSON.stringify(this.layout.serialize()));
    } catch { /* storage full/unavailable */ }
  }

  private loadPersisted(): WindowPersistence | null {
    try {
      const raw = localStorage.getItem(WINDOW_LAYOUT_KEY);
      return raw ? (JSON.parse(raw) as WindowPersistence) : null;
    } catch {
      return null;
    }
  }
}

const PAGE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" height="12" viewBox="0 -960 960 960" width="12" fill="currentColor" aria-hidden="true"><path d="M320-240h320v-80H320v80Zm0-160h320v-80H320v80ZM240-80q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h320l240 240v480q0 33-23.5 56.5T720-80H240Z"/></svg>`;
const COMPONENT_ICON = `<svg xmlns="http://www.w3.org/2000/svg" height="12" viewBox="0 -960 960 960" width="12" fill="currentColor" aria-hidden="true"><path d="M352-120H200q-33 0-56.5-23.5T120-200v-152q48 0 84-30.5t36-77.5q0-47-36-77.5T120-568v-152q0-33 23.5-56.5T200-800h160q0-42 29-71t71-29q42 0 71 29t29 71h160q33 0 56.5 23.5T800-720v160q42 0 71 29t29 71q0 42-29 71t-71 29v160q0 33-23.5 56.5T720-120H568q0-50-31.5-85T460-240q-45 0-76.5 35T352-120Z"/></svg>`;
